/**
 * Australian GST engine — the frontend PORT of the finance service's engine.
 *
 * ── This is a PORT, not an independent implementation ─────────────────────────────────────────
 *
 * The original is `finance/src/common/money/gst.ts` (repo
 * `code/xoe-clinic/finance`). The formulas, the rounding rule, the mode guard and the
 * per-line-then-sum order are copied deliberately, character for character where it matters, so
 * that the preview a practitioner sees while typing an invoice equals the figures the backend
 * will persist.
 *
 * The two are held together by ONE shared fixture table:
 *
 *   backend  : finance/src/common/money/gst-fixtures.json   (consumed by gst.spec.ts)
 *   frontend : src/utils/finance/gst-fixtures.json          (consumed by gst.test.ts)
 *
 * The frontend copy is byte-identical to the backend one, and `gst.test.ts` asserts that
 * byte-identity whenever the finance repo is checked out beside this one. A case added on either
 * side is therefore covered on both, and a divergence fails a test rather than producing an
 * invoice whose preview and stored totals disagree.
 *
 * ⚠️ **CLIENT TOTALS ARE A PREVIEW ONLY.** Nothing computed here is ever submitted as an amount.
 * The create/update request carries `unitPriceCents`, `quantity` and `taxCode`; the server
 * recomputes and every mutating response REPLACES whatever this module produced.
 *
 * PURE MODULE: no React, no antd, no imports at all. Integer-in / integer-out over minor units
 * (cents), because IEEE-754 binary floats cannot give exact decimal arithmetic and a legal tax
 * invoice must render identically forever.
 *
 * Rounding is **half away from zero** — the ATO's worked examples, and what Xero / MYOB / Cliniko
 * do. Bankers' rounding would disagree with every package a clinic reconciles against.
 */

/**
 * The tax treatment of a single invoice line. EXACTLY three — mirrors the backend's `TaxCode`
 * enum; adding a member here without a backend migration produces a 400 on create.
 */
export enum TaxCode {
	/** GST-free health service (ATO GST Act s38-7 / s38-10) — the common case in a clinic. */
	GST_FREE_HEALTH = "gst_free_health",
	/** Taxable supply at the standard rate. */
	GST = "gst",
	/** GST-free for a reason other than health. */
	GST_FREE_OTHER = "gst_free_other",
}

/** How the operator typed the unit price. A data-entry convention only — both figures are stored. */
export enum PriceEntryMode {
	INCLUSIVE = "inclusive",
	EXCLUSIVE = "exclusive",
}

/** The standard Australian GST rate, in basis points. 1000 bps = 10%. */
export const GST_RATE_BPS = 1000;

/**
 * The operator-facing label for each tax code. One place, so the create modal, the line editor
 * and the rendered document cannot disagree about what `gst_free_health` is called.
 */
export const TAX_CODE_LABELS: Record<TaxCode, string> = {
	[TaxCode.GST_FREE_HEALTH]: "GST-free (health)",
	[TaxCode.GST]: "GST 10%",
	[TaxCode.GST_FREE_OTHER]: "GST-free (other)",
};

/** The rate a tax code attracts, in basis points. The ONLY place a code becomes a rate. */
export function rateBpsForTaxCode(taxCode: TaxCode | string): number {
	return taxCode === TaxCode.GST ? GST_RATE_BPS : 0;
}

/**
 * Integer division rounding halves UP. `d` must be a positive integer.
 *
 * `Math.floor((2n + d) / (2d))` is the standard branch-free form. Do NOT "simplify" it to
 * `Math.round(n / d)` — `Math.round` rounds .5 towards +Infinity, so it is asymmetric for
 * negatives, and the intermediate `n / d` is a float.
 */
export function divRoundHalfUp(n: number, d: number): number {
	return Math.floor((2 * n + d) / (2 * d));
}

/**
 * `divRoundHalfUp` made sign-symmetric: a discount line of -$0.05 must round to exactly the
 * negative of what +$0.05 rounds to, or a discount and its reversal do not cancel.
 */
export function divRoundHalfAwayFromZero(n: number, d: number): number {
	return n < 0 ? -divRoundHalfUp(-n, d) : divRoundHalfUp(n, d);
}

/** One line's inputs. `qty` and `unitPriceCents` are integers; `unitPriceCents` may be negative. */
export interface GstLineInput {
	mode: PriceEntryMode | "inclusive" | "exclusive";
	rateBps: number;
	qty: number;
	unitPriceCents: number;
}

/** One line's three money figures. All integers, all in cents. */
export interface GstLineAmounts {
	lineExCents: number;
	lineTaxCents: number;
	lineIncCents: number;
}

/** An invoice's three money figures. All integers, all in cents. */
export interface GstTotals {
	subtotalExCents: number;
	taxCents: number;
	totalIncCents: number;
}

function assertInteger(name: string, value: number): void {
	if (!Number.isSafeInteger(value)) {
		throw new Error(`gst: ${name} must be a safe integer (got ${String(value)})`);
	}
}

/**
 * Collapse IEEE-754 negative zero to 0. A money figure of -0 is 0, but `Object.is(-0, 0)` is
 * false, so leaking it makes equality assertions behave inexplicably.
 */
function zeroSafe(n: number): number {
	return n === 0 ? 0 : n;
}

/**
 * Compute one line's ex / tax / inc, in cents.
 *
 *   INCLUSIVE:  inc = qty * unitPriceCents
 *               tax = round(inc * rateBps / (10000 + rateBps))   // 10% -> /11
 *               ex  = inc - tax                                  // subtract, never round twice
 *   EXCLUSIVE:  ex  = qty * unitPriceCents
 *               tax = round(ex * rateBps / 10000)                // 10% -> /10
 *               inc = ex + tax
 *
 * A zero rate short-circuits: no division, no rounding, ex === inc, tax === 0 — which is what
 * makes `gst_free_health` (the clinic default) exact by construction rather than by luck.
 *
 * Throws on a mode that is neither `inclusive` nor `exclusive`, INCLUDING at rate 0 where the
 * mode does not affect the answer. A bad mode is a bug wherever it comes from, and the point is
 * to hear about it on the GST-free line rather than three months later on a taxable one.
 */
export function computeLine(input: GstLineInput): GstLineAmounts {
	assertInteger("qty", input.qty);
	assertInteger("unitPriceCents", input.unitPriceCents);
	assertInteger("rateBps", input.rateBps);
	if (input.rateBps < 0) {
		throw new Error(`gst: rateBps must not be negative (got ${String(input.rateBps)})`);
	}

	// Checked BEFORE the rate-0 short-circuit, and by equality rather than by falling through to
	// "inclusive": a typo ("Inclusive", "incl", "") would otherwise silently compute the inclusive
	// answer for a line the operator entered exclusive.
	if (input.mode !== PriceEntryMode.INCLUSIVE && input.mode !== PriceEntryMode.EXCLUSIVE) {
		throw new Error(
			`gst: mode must be '${PriceEntryMode.INCLUSIVE}' or '${PriceEntryMode.EXCLUSIVE}' ` +
				`(got ${JSON.stringify(input.mode)})`,
		);
	}

	const base = zeroSafe(input.qty * input.unitPriceCents);

	if (input.rateBps === 0) {
		return { lineExCents: base, lineTaxCents: 0, lineIncCents: base };
	}

	if (input.mode === PriceEntryMode.EXCLUSIVE) {
		const lineExCents = base;
		const lineTaxCents = zeroSafe(divRoundHalfAwayFromZero(lineExCents * input.rateBps, 10000));
		return {
			lineExCents,
			lineTaxCents,
			lineIncCents: zeroSafe(lineExCents + lineTaxCents),
		};
	}

	const lineIncCents = base;
	const lineTaxCents = zeroSafe(
		divRoundHalfAwayFromZero(lineIncCents * input.rateBps, 10000 + input.rateBps),
	);
	return {
		lineExCents: zeroSafe(lineIncCents - lineTaxCents),
		lineTaxCents,
		lineIncCents,
	};
}

/**
 * Sum already-rounded lines. Round per line, THEN sum — never sum first and round once, which is
 * where the ATO's two methods diverge.
 */
export function sumLines(lines: GstLineAmounts[]): GstTotals {
	let subtotalExCents = 0;
	let taxCents = 0;
	let totalIncCents = 0;

	for (const line of lines) {
		subtotalExCents += line.lineExCents;
		taxCents += line.lineTaxCents;
		totalIncCents += line.lineIncCents;
	}

	return { subtotalExCents, taxCents, totalIncCents };
}

/** A draft line as the editor holds it, before it becomes a request body. */
export interface DraftLine {
	quantity: number;
	unitPriceCents: number;
	taxCode: TaxCode | string;
}

/**
 * Preview one draft line. A line the operator has not finished typing (no price yet, blank
 * quantity) previews as zeroes rather than throwing — the editor must stay usable mid-keystroke,
 * and the strict guard in `computeLine` still protects the mode.
 */
export function previewLine(line: DraftLine, mode: PriceEntryMode): GstLineAmounts {
	const qty = Number.isSafeInteger(line.quantity) ? line.quantity : 0;
	const unitPriceCents = Number.isSafeInteger(line.unitPriceCents) ? line.unitPriceCents : 0;

	return computeLine({
		mode,
		rateBps: rateBpsForTaxCode(line.taxCode),
		qty,
		unitPriceCents,
	});
}

/** Preview a whole draft invoice. Per-line then sum, exactly as the backend does it. */
export function previewTotals(lines: DraftLine[], mode: PriceEntryMode): GstTotals {
	return sumLines(lines.map((line) => previewLine(line, mode)));
}
